import { Theme } from '@logto/schemas';
import { conditionalString } from '@silverhand/essentials';
import classNames from 'classnames';
import i18next from 'i18next';
import { useContext } from 'react';
import { Helmet } from 'react-helmet';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import defaultAppleTouchLogo from '@/shared/assets/apple-touch-icon.png';
import defaultFavicon from '@/shared/assets/favicon.png';
import { type SignInExperienceResponse } from '@/types';

import styles from './index.module.scss';

const themeToFavicon = Object.freeze({
  [Theme.Light]: 'favicon',
  [Theme.Dark]: 'darkFavicon',
} as const satisfies Record<Theme, keyof SignInExperienceResponse['branding']>);

/**
 * User React Helmet to manage html and body attributes
 * @see https://github.com/nfl/react-helmet
 *
 * - lang: set html lang attribute
 * - data-theme: set html data-theme attribute
 * - favicon: set favicon
 * - apple-touch-icon: set apple touch icon
 * - body class: set preview body class
 * - body class: set platform body class
 * - body class: set theme body class
 * - custom css: set custom css style tag
 */

const AppMeta = () => {
  const { experienceSettings, theme, platform, isPreview } = useContext(PageContext);
  const favicon =
    experienceSettings?.branding[themeToFavicon[theme]] ?? experienceSettings?.branding.favicon;

  // TPB Branding: Signature effects CSS
  const tpbBrandingCss = `
    /* TPB Shimmer Effect */
    @keyframes tpb-shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    .tpb-shimmer { position: relative; overflow: hidden; }
    .tpb-shimmer::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent);
      background-size: 200% 100%;
      animation: tpb-shimmer 2s infinite linear;
      pointer-events: none;
      border-radius: inherit;
    }
    
    /* TPB Shine Text Effect */
    @keyframes tpb-shine {
      0% { background-position: 200% center; }
      100% { background-position: -200% center; }
    }
    .tpb-shiny-text {
      background-image: linear-gradient(90deg, #a1a1aa 35%, #fff 50%, #a1a1aa 65%);
      background-size: 200% auto;
      background-clip: text;
      -webkit-background-clip: text;
      color: transparent;
      animation: tpb-shine 5s linear infinite;
    }
    
    /* TPB Hover Halo */
    .tpb-hover-halo { transition: box-shadow 0.2s ease, background-color 0.2s ease; }
    .tpb-hover-halo:hover {
      box-shadow: 0 0 12px 2px rgba(255, 255, 255, 0.08);
      background-color: rgba(255, 255, 255, 0.05);
    }
    
    /* TPB Pulse Glow */
    @keyframes tpb-pulse-glow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(0, 212, 255, 0); }
      50% { box-shadow: 0 0 20px 4px rgba(0, 212, 255, 0.4); }
    }
    .tpb-pulse-glow { animation: tpb-pulse-glow 2s ease-in-out infinite; }
  `;

  return (
    <Helmet>
      <html lang={i18next.language} dir={i18next.dir()} data-theme={theme} />
      <link rel="shortcut icon" href={favicon ?? defaultFavicon} />
      <link rel="apple-touch-icon" href={favicon ?? defaultAppleTouchLogo} sizes="180x180" />
      {/* TPB Branding: Google Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&display=swap"
        rel="stylesheet"
      />
      {/* TPB Branding: Signature Effects */}
      <style>{tpbBrandingCss}</style>
      {experienceSettings?.customCss && <style>{experienceSettings.customCss}</style>}
      <body
        className={classNames(
          conditionalString(isPreview && styles.preview),
          platform === 'mobile' ? 'mobile' : 'desktop',
          conditionalString(styles[theme])
        )}
      />
    </Helmet>
  );
};

export default AppMeta;
